/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { FormEvent, KeyboardEvent } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight"
import { Color } from "@tiptap/extension-color"
import FontFamily from "@tiptap/extension-font-family"
import Highlight from "@tiptap/extension-highlight"
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import { Table } from "@tiptap/extension-table"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import TableRow from "@tiptap/extension-table-row"
import TaskItem from "@tiptap/extension-task-item"
import TaskList from "@tiptap/extension-task-list"
import TextAlign from "@tiptap/extension-text-align"
import { TextStyle } from "@tiptap/extension-text-style"
import Underline from "@tiptap/extension-underline"
import { EditorContent, ReactNodeViewRenderer, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { formatDistanceToNow } from "date-fns"
import { common, createLowlight } from "lowlight"
import { DOMParser } from "prosemirror-model"
import { useTranslation } from "react-i18next"
import { useBlocker, useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { PendingSuggestion } from "./ai-edit-bubble"
import type {
  AiEditHighlightMeta,
  AiEditHighlightState,
  AiEditSuggestion,
} from "../tiptap/ai-edit-highlight"
import type { SelectionEditTarget } from "../utils/selection-edit-utils"
import type { TextEnhancementResponse } from "@/api/api"
import type { DocumentVersion, GraphFileItem, UserDocument } from "@/api/models"
import type { CanvasVersion } from "@/components/canvas-editor/version-history-dropdown"
import type { Node as ProseMirrorNode } from "prosemirror-model"

import {
  addEmojisApi,
  adjustLengthApi,
  adjustReadingLevelApi,
  downloadGraphFileApi,
  downloadResponseApi,
  getCitationFilePath,
  polishTextApi,
  suggestEditApi,
  updateDocumentApi,
  updateMessageApi,
} from "@/api/api"
import { DownloadMenu } from "@/components/canvas-editor/download-menu"
import {
  ArrowLeft,
  Check,
  Code,
  Copy,
  Eye,
  History,
  Loader2,
  Maximize2,
  MessageCirclePlus,
  Minimize2,
  MoreHorizontal,
  Pencil,
  Share2,
  Sparkles,
  Trash2,
  Type,
  X,
} from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { DiffPreviewDialog } from "@/components/text-enhancement/DiffPreviewDialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useArtifactLock } from "@/features/artifacts/hooks/use-artifact-lock"
import {
  parseAnswerToHtml,
  replaceCitationsWithLinks,
} from "@/features/chat/components/answer-parser"
import { createCitationLinkComponent } from "@/features/chat/components/citation-link"
import { CitationSidebarHeader } from "@/features/chat/components/citation-sidebar-header"
import { CitationViewerContent } from "@/features/chat/components/citation-viewer-content"
import { getCitationType } from "@/features/chat/components/citation.utils"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import answerStyles from "@/features/chat/styles/answer.module.css"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useDateLocale } from "@/hooks/use-date-locale"
import { useDeleteDocument } from "@/hooks/use-documents"
import i18n from "@/languageConfig"
import { queryKeys } from "@/lib/query-keys"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import { parseCitationParts, resolveCitationDisplayTitle } from "@/utils/citation-utils"
import { normalizeForMatching } from "@/utils/normalize-for-matching"

import { AiEditBubble } from "./ai-edit-bubble"
import { CanvasHistoryPanel } from "./canvas-history-panel"
import { CanvasReferencesPanel } from "./canvas-references-panel"
import { FormatToolbar } from "./format-toolbar"
import { InlineSuggestionActions } from "./inline-suggestion-actions"
import { MobileFormatBar } from "./mobile-format-bar"
import { useCanvasStore } from "../stores/canvas-store"
import {
  AiEditHighlight,
  aiEditHighlightKey,
  getSuggestionAtPosition,
  isSelectionOverlappingAnySuggestion,
} from "../tiptap/ai-edit-highlight"
import { AiSelectionHighlight } from "../tiptap/ai-selection-highlight"
import { CanvasCitation } from "../tiptap/citation-extension"
import { CodeBlockNodeView } from "../tiptap/code-block-node-view"
import { FontSize } from "../tiptap/font-size-extension"
import { CanvasKeyboardShortcuts } from "../tiptap/keyboard-shortcuts"
import { markdownToTiptapHtml, markdownToTiptapHtmlInline } from "../tiptap/markdown-to-tiptap-html"
import { MathematicsExtension } from "../tiptap/mathematics-extension"
import { SessionImageNodeView } from "../tiptap/session-image-node-view"
import { createCanvasTurndownService } from "../utils/canvas-turndown"
import {
  applyCellReplacements,
  buildRangeReplacement,
  findCellDepth,
  getSelectionEditTarget,
  isMultiCellTarget,
  markdownToInsertableHtml,
  normalizeInsertedMarkdown,
  parseResponseIntoCellValues,
  selectionCrossesTableCells,
  serializeRangeToHtml,
} from "../utils/selection-edit-utils"

const lowlight = createLowlight(common)
const REMOTE_UPDATE_HIGHLIGHT_ID = "remote-update-highlight"

function writeDocumentToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  updatedDocument: UserDocument
) {
  const { id } = updatedDocument
  queryClient.setQueryData(queryKeys.document(id), updatedDocument)
  if (updatedDocument.versions != null) {
    queryClient.setQueryData(queryKeys.documentVersions(id), updatedDocument.versions)
  }
  queryClient.setQueriesData(
    { queryKey: queryKeys.documents(), exact: false },
    (old: UserDocument[] | undefined) => old?.map((d) => (d.id === id ? updatedDocument : d)) ?? old
  )
}

type RemotePreviewSnapshot = {
  html: string
  markdown: string
  isDirty: boolean
}

type AiTextActionLabels = {
  operationName: string
  suggestionMessage: string
  failureMessage: string
}

/** The selected range an AI action runs on, captured when the request is sent. */
type ScopedAiEditSource = {
  target: SelectionEditTarget
  doc: ProseMirrorNode
  originalHtml: string
  originalMarkdown: string
  originalCellTexts: string[]
}

/** A selection-scoped AI result waiting for the user to review its diff. */
type ScopedAiEdit = ScopedAiEditSource & AiTextActionLabels & { modifiedText: string }

export type CanvasPaneProps = {
  isPhone?: boolean
  isActiveView?: boolean
  canEditDocument?: boolean
  onBackToDocuments?: () => void
  onReturnToChat?: () => void
  backButtonLabel?: string
  onStartNewChatWithDocument?: () => void
  startChatButtonLabel?: string
  onShareDocument?: () => void
  canShareDocument?: boolean
  canDeleteDocument?: boolean
}

export function CanvasPane({
  isPhone = false,
  isActiveView = true,
  canEditDocument = true,
  onBackToDocuments,
  onReturnToChat,
  backButtonLabel,
  onStartNewChatWithDocument,
  startChatButtonLabel,
  onShareDocument,
  canShareDocument = false,
  canDeleteDocument = false,
}: CanvasPaneProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const dateLocale = useDateLocale()
  const { data: config } = useConfigQuery()
  const queryClient = useQueryClient()
  const deleteDocumentMutation = useDeleteDocument()
  const {
    appendVersion,
    content,
    disablePersistence,
    documentId,
    etag,
    messageId,
    activeDocumentCanEdit,
    lastSavedAt,
    selectedModelId,
    sessionId,
    sessionFilenames,
    imageContentBaseUrl,
    pendingRemoteUpdate,
    applyRemoteUpdate,
    dismissRemoteUpdate,
    setAutosaveStatus,
    setContent,
    setEtag,
    setIsDirty: setStoreDirty,
    setLastSavedAt,
    setActiveSelection,
    setTitle,
    setVersions,
    title,
    versions,
    loadRevision,
    autosaveStatus,
  } = useCanvasStore()
  const isFocused = useContentSidebarStore(
    (state) => state.isFocused && state.currentPanel?.id === "canvas"
  )
  const isCanvasOpen = useContentSidebarStore(
    (state) => state.isOpen && state.currentPanel?.id === "canvas"
  )
  const closeSidebar = useContentSidebarStore((state) => state.closePanel)
  const requestSidebarClose = useContentSidebarStore((state) => state.requestClose)
  const toggleSidebarFocus = useContentSidebarStore((state) => state.toggleFocus)

  const [isDirty, setIsDirty] = useState(false)
  const [showDeleteDocumentDialog, setShowDeleteDocumentDialog] = useState(false)

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isCanvasOpen && isDirty && currentLocation.pathname !== nextLocation.pathname
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [showFormatToolbar, setShowFormatToolbar] = useState(true)
  const [viewMode, setViewMode] = useState<"edit" | "preview" | "raw">("edit")
  const [isAiProcessing, setIsAiProcessing] = useState(false)
  const [showFilePicker, setShowFilePicker] = useState(false)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [isRenamingTitle, setIsRenamingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(title)
  const [isSavingTitle, setIsSavingTitle] = useState(false)
  const lastLoadedEntityIdRef = useRef<string | null>(null)
  const lastLoadedRevisionRef = useRef<number | null>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)

  const [pendingSuggestions, setPendingSuggestions] = useState<Map<string, PendingSuggestion>>(
    () => new Map()
  )
  const [activeSuggestion, setActiveSuggestion] = useState<AiEditSuggestion | null>(null)
  const [showRemoteDiffDialog, setShowRemoteDiffDialog] = useState(false)
  const [scopedAiEdit, setScopedAiEdit] = useState<ScopedAiEdit | null>(null)
  const remotePreviewSnapshotRef = useRef<RemotePreviewSnapshot | null>(null)
  const hasAppliedRemotePreviewRef = useRef(false)

  // Check-out edit lock: acquire when this user would otherwise be able to edit a
  // document-backed canvas. If another user holds it, force read-only + banner.
  const wouldEdit = canEditDocument && (!documentId || activeDocumentCanEdit)
  const artifactLock = useArtifactLock(documentId ?? null, wouldEdit && Boolean(documentId))
  const lockedByOther = artifactLock.status === "locked-by-other"
  const canEditCurrentDocument = wouldEdit && !lockedByOther
  const pendingCount = pendingSuggestions.size
  const canRenameTitle = Boolean(documentId) && canEditCurrentDocument
  const isDocumentMode = Boolean(onBackToDocuments && location.pathname.startsWith("/documents/"))
  const showStartNewChatWithDocument = Boolean(onStartNewChatWithDocument && isDocumentMode)
  const renameTitleButtonClassName = cn(
    "group/title inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 transition-colors",
    "hover:bg-muted/60 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
    isPhone && "mx-auto"
  )

  const mapDocumentVersionsToCanvas = useCallback(
    (documentVersions: DocumentVersion[] | undefined): CanvasVersion[] => {
      return (documentVersions ?? []).map((version) => ({
        version: version.version,
        content: version.content,
        timestamp: version.timestamp,
        is_ai_edit: version.edit_source === "ai",
        ai_edit_preview: version.edit_preview,
      }))
    },
    []
  )

  const turndownService = useMemo(() => {
    const service = createCanvasTurndownService()

    service.addRule("mathBlock", {
      filter: (node) =>
        node.nodeName === "DIV" && (node as HTMLElement).classList.contains("math-block"),
      replacement: (_content, node) => {
        const latex = (node as HTMLElement).getAttribute("data-latex") ?? ""
        return `\n\n$$\n${latex}\n$$\n\n`
      },
    })

    service.addRule("mathInline", {
      filter: (node) =>
        node.nodeName === "SPAN" && (node as HTMLElement).classList.contains("math-inline"),
      replacement: (_content, node) => {
        const latex = (node as HTMLElement).getAttribute("data-latex") ?? ""
        return `$${latex}$`
      },
    })

    service.addRule("canvasCitation", {
      filter: (node) =>
        node.nodeName === "SPAN" && (node as HTMLElement).hasAttribute("data-canvas-citation"),
      replacement: (_content, node) => {
        const citation = (node as HTMLElement).getAttribute("data-canvas-citation") ?? ""
        return `[[${citation}]]`
      },
    })

    service.addRule("styledSpan", {
      filter: (node) => {
        if (node.nodeName !== "SPAN") return false
        const element = node as HTMLElement
        if (element.classList.contains("math-inline")) return false
        if (element.hasAttribute("data-canvas-citation")) return false
        return Boolean(element.getAttribute("style"))
      },
      replacement: (content, node) => {
        const element = node as HTMLElement
        const style = element.getAttribute("style") ?? ""
        return `<span style="${style}">${content}</span>`
      },
    })

    service.addRule("highlightMark", {
      filter: (node) => node.nodeName === "MARK",
      replacement: (content, node) => {
        const element = node as HTMLElement
        const style = element.getAttribute("style") ?? ""
        const dataColor = element.getAttribute("data-color") ?? ""
        if (style) {
          return `<mark style="${style}">${content}</mark>`
        }
        if (dataColor) {
          return `<mark data-color="${dataColor}">${content}</mark>`
        }
        return `<mark>${content}</mark>`
      },
    })

    service.addRule("underline", {
      filter: "u",
      replacement: (content) => `<u>${content}</u>`,
    })

    service.addRule("textAlignParagraph", {
      filter: (node) => {
        if (node.nodeName !== "P") return false
        const element = node as HTMLElement
        const style = element.getAttribute("style") ?? ""
        return style.includes("text-align")
      },
      replacement: (content, node) => {
        const element = node as HTMLElement
        const style = element.getAttribute("style") ?? ""
        return `<p style="${style}">${content}</p>\n\n`
      },
    })

    service.addRule("styledLink", {
      filter: (node) =>
        node.nodeName === "A" && Boolean((node as HTMLElement).getAttribute("href")),
      replacement: (content, node) => {
        const element = node as HTMLElement
        const href = element.getAttribute("href") ?? ""
        return `[${content}](${href})`
      },
    })

    service.addRule("taskList", {
      filter: (node) =>
        node.nodeName === "UL" && (node as HTMLElement).getAttribute("data-type") === "taskList",
      replacement: (content, node) => {
        const parent = node.parentNode as HTMLElement | null
        // Check if nested inside a task item's div (TipTap uses data-checked, not data-type for li)
        const grandparent = parent?.parentNode as HTMLElement | null
        const isNested =
          grandparent !== null &&
          grandparent.nodeName === "LI" &&
          grandparent.hasAttribute("data-checked")
        if (isNested) {
          const indentedContent = content
            .split("\n")
            .map((line) => (line.trim() ? `  ${line}` : line))
            .join("\n")
          return `\n${indentedContent}`
        }
        return `\n${content}\n`
      },
    })

    service.addRule("taskItem", {
      // TipTap uses data-checked attribute on li, not data-type="taskItem"
      filter: (node) =>
        node.nodeName === "LI" && (node as HTMLElement).hasAttribute("data-checked"),
      replacement: (content, node) => {
        const element = node as HTMLElement
        const isChecked = element.getAttribute("data-checked") === "true"
        const checkbox = isChecked ? "[x]" : "[ ]"
        const directDiv = element.querySelector(":scope > div")
        // Get only the direct text from the first p, not nested content
        const firstP = directDiv?.querySelector(":scope > p")
        const textContent = (firstP?.textContent ?? "").trim()
        const nestedContent = content.includes("- [")
          ? content.substring(content.indexOf("\n"))
          : ""
        return `- ${checkbox} ${textContent}${nestedContent}\n`
      },
    })

    return service
  }, [])

  const lastUpdatedLabel = useMemo(() => {
    const latestTimestamp =
      lastSavedAt ??
      versions
        .map((version) => version.timestamp)
        .filter(Boolean)
        .sort()
        .at(-1)

    if (!latestTimestamp) {
      return t("canvas.updated_just_now", { defaultValue: "Updated just now" })
    }

    return t("canvas.updated_relative", {
      defaultValue: "Updated {{relative}}",
      relative: formatDistanceToNow(new Date(latestTimestamp), {
        addSuffix: true,
        locale: dateLocale,
      }),
    })
  }, [lastSavedAt, versions, dateLocale, t])

  const isAutosaveSaving = autosaveStatus === "saving" || isSaving

  const autosaveLabel = useMemo(() => {
    if (isAutosaveSaving) {
      return t("canvas.autosave_saving", { defaultValue: "Saving..." })
    }
    if (autosaveStatus === "error") {
      return t("canvas.autosave_error", { defaultValue: "Autosave failed" })
    }
    if (isDirty) {
      return t("canvas.autosave_unsaved", { defaultValue: "Unsaved changes" })
    }
    return t("canvas.autosave_saved", { defaultValue: "All changes saved" })
  }, [autosaveStatus, isAutosaveSaving, isDirty, t])

  useEffect(() => {
    if (!canEditCurrentDocument && viewMode !== "preview") {
      setViewMode("preview")
    }
  }, [canEditCurrentDocument, viewMode])

  useEffect(() => {
    if (!isRenamingTitle) {
      setTitleDraft(title)
    }
  }, [isRenamingTitle, title])

  useEffect(() => {
    if (!isRenamingTitle) return
    const timeout = window.setTimeout(() => {
      const input = titleInputRef.current
      if (input) {
        input.focus()
        input.select()
      }
    }, 0)
    return () => window.clearTimeout(timeout)
  }, [isRenamingTitle])

  const handleStartTitleRename = useCallback(() => {
    if (!canRenameTitle || isSavingTitle) return
    setTitleDraft(title)
    setIsRenamingTitle(true)
  }, [canRenameTitle, isSavingTitle, title])

  const handleCancelTitleRename = useCallback(() => {
    setTitleDraft(title)
    setIsRenamingTitle(false)
  }, [title])

  const handleSubmitTitleRename = useCallback(async () => {
    if (!documentId || isSavingTitle) return

    const trimmedTitle = titleDraft.trim()
    if (!trimmedTitle) {
      toast.error(
        t("canvas.rename_title_required", { defaultValue: "Document name is required" }),
        {
          description: t("canvas.rename_title_required_description", {
            defaultValue: "Please enter a name before saving.",
          }),
        }
      )
      return
    }

    if (trimmedTitle === title.trim()) {
      setIsRenamingTitle(false)
      return
    }

    setIsSavingTitle(true)
    try {
      const settingsService = SettingsService.getInstance()
      const updatedDocument = await updateDocumentApi(
        documentId,
        { title: trimmedTitle },
        settingsService.token
      )
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const nextTitle = updatedDocument.title?.trim() || trimmedTitle
      setTitle(nextTitle)
      setTitleDraft(nextTitle)
      setLastSavedAt(updatedDocument.updated ?? new Date().toISOString())
      setEtag((updatedDocument as UserDocument & { _etag?: string })._etag ?? etag)
      setIsRenamingTitle(false)
      writeDocumentToCache(queryClient, updatedDocument)
    } catch (error) {
      toast.error(t("canvas.rename_title_failed", { defaultValue: "Failed to rename document" }), {
        description:
          error instanceof Error
            ? error.message
            : t("canvas.rename_title_failed_description", {
                defaultValue: "Please try again.",
              }),
      })
    } finally {
      setIsSavingTitle(false)
    }
  }, [
    documentId,
    etag,
    isSavingTitle,
    queryClient,
    setEtag,
    setLastSavedAt,
    setTitle,
    t,
    title,
    titleDraft,
  ])

  const handleTitleRenameFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      void handleSubmitTitleRename()
    },
    [handleSubmitTitleRename]
  )

  const handleTitleRenameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        event.preventDefault()
        handleCancelTitleRename()
      }
    },
    [handleCancelTitleRename]
  )

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        codeBlock: false,
      }),
      CodeBlockLowlight.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeBlockNodeView)
        },
      }).configure({ lowlight }),
      TaskList,
      TaskItem.configure({ nested: true }),
      MathematicsExtension,
      CanvasCitation,
      AiEditHighlight,
      AiSelectionHighlight,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Image.extend({
        addNodeView() {
          return ReactNodeViewRenderer(SessionImageNodeView)
        },
      }).configure({
        inline: false,
        allowBase64: true,
      }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Underline,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline cursor-pointer",
        },
      }),
      CanvasKeyboardShortcuts,
    ],
    content: "",
    onUpdate: ({ editor: updatedEditor }) => {
      const html = updatedEditor.getHTML()
      const markdown = turndownService.turndown(html)
      setContent(markdown)
      setIsDirty(true)
    },
    onSelectionUpdate: ({ editor: updatedEditor }) => {
      const { from, to } = updatedEditor.state.selection
      if (from === to) {
        setActiveSelection("", null, null)
      } else {
        const selectedText = updatedEditor.state.doc.textBetween(from, to, " ")
        setActiveSelection(selectedText, from, to)
      }

      const highlightState = aiEditHighlightKey.getState(
        updatedEditor.state
      ) as AiEditHighlightState | null
      if (!highlightState || highlightState.size === 0) {
        setActiveSuggestion(null)
        return
      }

      const cursorPos = updatedEditor.state.selection.head
      const suggestion = getSuggestionAtPosition(highlightState, cursorPos)
      setActiveSuggestion(suggestion)
    },
  })

  useEffect(() => {
    setStoreDirty(isDirty)
  }, [isDirty, setStoreDirty])

  useEffect(() => {
    if (isDirty) {
      setAutosaveStatus("idle")
    }
  }, [isDirty, setAutosaveStatus])

  useEffect(() => {
    if (!editor) return
    const entityId = documentId ?? messageId
    if (!entityId) return
    const shouldReloadEntity = lastLoadedEntityIdRef.current !== entityId
    const shouldReloadRevision = lastLoadedRevisionRef.current !== loadRevision
    if (!shouldReloadEntity && !shouldReloadRevision) return

    const html = markdownToTiptapHtml(
      content || "",
      sessionId || imageContentBaseUrl
        ? {
            sessionId: sessionId ?? "",
            filenames: sessionFilenames,
            contentBaseUrl: imageContentBaseUrl ?? undefined,
          }
        : undefined
    )
    editor.commands.setContent(html, { emitUpdate: false })
    lastLoadedEntityIdRef.current = entityId
    lastLoadedRevisionRef.current = loadRevision
    setIsDirty(false)
    setIsHistoryOpen(false)
    setPendingSuggestions(new Map())
    setActiveSuggestion(null)
    setActiveSelection("", null, null)
  }, [
    editor,
    documentId,
    messageId,
    content,
    sessionId,
    sessionFilenames,
    loadRevision,
    setActiveSelection,
  ])

  const handleSuggestionCreated = useCallback(
    (suggestion: PendingSuggestion) => {
      if (!editor) return
      const html = editor.getHTML()
      const markdown = turndownService.turndown(html)
      setContent(markdown)
      setIsDirty(true)

      setPendingSuggestions((prev) => {
        const next = new Map(prev)
        next.set(suggestion.id, suggestion)
        return next
      })

      const nowIso = new Date().toISOString()
      const nextVersion =
        versions.length > 0 ? Math.max(...versions.map((version) => version.version)) + 1 : 1
      appendVersion({
        version: nextVersion,
        content: markdown,
        timestamp: nowIso,
        is_ai_edit: true,
        ai_edit_preview: suggestion.modifiedText.slice(0, 80),
      })
    },
    [editor, turndownService, setContent, versions, appendVersion]
  )

  const acceptSuggestion = useCallback(
    (suggestionId: string) => {
      if (!editor) return

      const meta: AiEditHighlightMeta = { action: "remove", id: suggestionId }
      editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))

      setPendingSuggestions((prev) => {
        const next = new Map(prev)
        next.delete(suggestionId)
        return next
      })

      if (activeSuggestion?.id === suggestionId) {
        setActiveSuggestion(null)
      }
    },
    [editor, activeSuggestion]
  )

  const discardSuggestion = useCallback(
    (suggestionId: string) => {
      if (!editor) return

      const suggestion = pendingSuggestions.get(suggestionId)
      if (!suggestion) return

      const isMultiCell = suggestion.cellRanges && suggestion.cellRanges.length > 1

      if (isMultiCell && suggestion.originalCellTexts) {
        const { state, view } = editor
        let tr = state.tr
        const sortedRanges = [...suggestion.cellRanges!].sort((a, b) => b.from - a.from)

        for (const [index, cellRange] of sortedRanges.entries()) {
          const valueIndex = suggestion.cellRanges!.length - 1 - index
          const cellText = suggestion.originalCellTexts[valueIndex] ?? ""
          const cellHtml = markdownToTiptapHtmlInline(cellText)
          const $from = tr.doc.resolve(tr.mapping.map(cellRange.from))
          const cellDepth = findCellDepth($from)
          if (cellDepth === -1) continue

          const $to = tr.doc.resolve(tr.mapping.map(cellRange.to))
          const tempContainer = document.createElement("div")
          tempContainer.innerHTML = cellHtml
          const parsedSlice = DOMParser.fromSchema(state.schema).parseSlice(tempContainer)
          tr = tr.replaceRange($from.start(cellDepth), $to.end(cellDepth), parsedSlice)
        }
        view.dispatch(tr)
      } else {
        const highlightState = aiEditHighlightKey.getState(
          editor.state
        ) as AiEditHighlightState | null
        const currentRanges = highlightState?.get(suggestionId)
        const currentRange = currentRanges?.[0]

        if (currentRange) {
          const { state, view } = editor
          const tr = state.tr
          const tempContainer = document.createElement("div")
          tempContainer.innerHTML = suggestion.originalHtml
          const parser = DOMParser.fromSchema(state.schema)
          const parsedSlice = parser.parseSlice(tempContainer)
          tr.replaceRange(currentRange.from, currentRange.to, parsedSlice)
          view.dispatch(tr)
        }
      }

      const meta: AiEditHighlightMeta = { action: "remove", id: suggestionId }
      editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))

      setPendingSuggestions((prev) => {
        const next = new Map(prev)
        next.delete(suggestionId)
        return next
      })

      if (activeSuggestion?.id === suggestionId) {
        setActiveSuggestion(null)
      }

      const html = editor.getHTML()
      const markdown = turndownService.turndown(html)
      setContent(markdown)
    },
    [editor, pendingSuggestions, activeSuggestion, turndownService, setContent]
  )

  const acceptAllSuggestions = useCallback(() => {
    if (!editor) return

    const meta: AiEditHighlightMeta = { action: "clear" }
    editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))

    setPendingSuggestions(new Map())
    setActiveSuggestion(null)
  }, [editor])

  const discardAllSuggestions = useCallback(() => {
    if (!editor) return

    const suggestionIds = Array.from(pendingSuggestions.keys())

    const highlightState = aiEditHighlightKey.getState(editor.state) as AiEditHighlightState | null
    if (!highlightState) return

    const sortedIds = suggestionIds
      .map((id) => ({ id, ranges: highlightState.get(id) }))
      .filter((entry) => entry.ranges !== undefined && entry.ranges.length > 0)
      .sort((entryA, entryB) => {
        const minFromA = Math.min(...entryA.ranges!.map((r) => r.from))
        const minFromB = Math.min(...entryB.ranges!.map((r) => r.from))
        return minFromB - minFromA
      })
      .map((entry) => entry.id)

    for (const suggestionId of sortedIds) {
      const suggestion = pendingSuggestions.get(suggestionId)
      if (!suggestion) continue

      const isMultiCell = suggestion.cellRanges && suggestion.cellRanges.length > 1

      if (isMultiCell && suggestion.originalCellTexts) {
        const { state, view } = editor
        let tr = state.tr
        const sortedRanges = [...suggestion.cellRanges!].sort((a, b) => b.from - a.from)

        for (const [index, cellRange] of sortedRanges.entries()) {
          const valueIndex = suggestion.cellRanges!.length - 1 - index
          const cellText = suggestion.originalCellTexts[valueIndex] ?? ""
          const cellHtml = markdownToTiptapHtmlInline(cellText)
          const $from = tr.doc.resolve(tr.mapping.map(cellRange.from))
          const cellDepth = findCellDepth($from)
          if (cellDepth === -1) continue

          const $to = tr.doc.resolve(tr.mapping.map(cellRange.to))
          const tempContainer = document.createElement("div")
          tempContainer.innerHTML = cellHtml
          const parsedSlice = DOMParser.fromSchema(state.schema).parseSlice(tempContainer)
          tr = tr.replaceRange($from.start(cellDepth), $to.end(cellDepth), parsedSlice)
        }
        view.dispatch(tr)
      } else {
        const currentHighlightState = aiEditHighlightKey.getState(
          editor.state
        ) as AiEditHighlightState | null
        const currentRanges = currentHighlightState?.get(suggestionId)
        const currentRange = currentRanges?.[0]

        if (currentRange) {
          const { state, view } = editor
          const tr = state.tr
          const tempContainer = document.createElement("div")
          tempContainer.innerHTML = suggestion.originalHtml
          const parser = DOMParser.fromSchema(state.schema)
          const parsedSlice = parser.parseSlice(tempContainer)
          tr.replaceRange(currentRange.from, currentRange.to, parsedSlice)
          view.dispatch(tr)
        }
      }
    }

    const meta: AiEditHighlightMeta = { action: "clear" }
    editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))

    setPendingSuggestions(new Map())
    setActiveSuggestion(null)

    const html = editor.getHTML()
    const markdown = turndownService.turndown(html)
    setContent(markdown)
  }, [editor, pendingSuggestions, turndownService, setContent])

  const handleRestoreVersion = useCallback(
    (version: CanvasVersion) => {
      if (!editor) return
      const html = markdownToTiptapHtml(version.content || "")
      editor.commands.setContent(html, { emitUpdate: false })
      setContent(version.content)
      setIsDirty(true)
      setIsHistoryOpen(false)

      const meta: AiEditHighlightMeta = { action: "clear" }
      editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))
      setPendingSuggestions(new Map())
      setActiveSuggestion(null)
    },
    [editor, setContent]
  )

  const performSave = useCallback(async () => {
    // In-memory (artifact) canvases have no backing server resource — never persist.
    if (disablePersistence) return
    if (!canEditCurrentDocument) return
    if (!editor) return
    if (!documentId && !messageId) return

    setIsSaving(true)
    setAutosaveStatus("saving")
    try {
      const settingsService = SettingsService.getInstance()
      const html = editor.getHTML()
      const markdown = turndownService.turndown(html)

      if (documentId) {
        const updatedDocument = await updateDocumentApi(
          documentId,
          {
            content: markdown,
            edit_source: "user",
            create_version: true,
          },
          settingsService.token
        )

        setVersions(mapDocumentVersionsToCanvas(updatedDocument.versions))
        setContent(updatedDocument.content || markdown)
        setEtag((updatedDocument as UserDocument & { _etag?: string })._etag ?? etag)
        setLastSavedAt(updatedDocument.updated ?? new Date().toISOString())
        writeDocumentToCache(queryClient, updatedDocument)
      } else if (messageId) {
        await updateMessageApi(messageId, markdown, settingsService.token, undefined, true)

        const nowIso = new Date().toISOString()
        const contentBeforeSave = content
        const newVersions =
          versions.length > 0
            ? [
                ...versions,
                {
                  version: Math.max(...versions.map((version) => version.version)) + 1,
                  content: markdown,
                  timestamp: nowIso,
                },
              ]
            : [
                {
                  version: 1,
                  content: contentBeforeSave,
                  timestamp: nowIso,
                  is_original: true,
                },
                {
                  version: 2,
                  content: markdown,
                  timestamp: nowIso,
                },
              ]

        setVersions(newVersions)
        setContent(markdown)
        setLastSavedAt(nowIso)
        useCanvasStore.getState().onMessageUpdate?.()
      }

      setIsDirty(false)
      setAutosaveStatus("saved")
    } catch (error) {
      setAutosaveStatus("error")
      const errorMessage = error instanceof Error ? error.message : ""
      const isSizeLimit =
        errorMessage.includes("413") || errorMessage.includes("maximum storage size")
      toast.error(
        isSizeLimit
          ? t("canvas.document_too_large", { defaultValue: "Document too large" })
          : t("canvas.autosave_error", { defaultValue: "Autosave failed" }),
        {
          description: isSizeLimit
            ? t("canvas.document_too_large_description", {
                defaultValue:
                  "This document exceeded the storage size limit. Version history has been trimmed automatically. Please try saving again.",
              })
            : errorMessage ||
              t("canvas.autosave_error_description", {
                defaultValue: "Could not save your latest edits",
              }),
        }
      )
    } finally {
      setIsSaving(false)
    }
  }, [
    disablePersistence,
    canEditCurrentDocument,
    documentId,
    messageId,
    editor,
    turndownService,
    content,
    versions,
    setVersions,
    setContent,
    mapDocumentVersionsToCanvas,
    setEtag,
    etag,
    setLastSavedAt,
    setAutosaveStatus,
    queryClient,
    t,
  ])

  useEffect(() => {
    if (!canEditCurrentDocument) return
    // In-memory (artifact) canvases: skip arming the autosave timer entirely so
    // the user never sees "saving / saved / error" indicators for edits that
    // have no backing server resource.
    if (disablePersistence) return
    if (!isCanvasOpen || !isDirty || pendingCount > 0 || pendingRemoteUpdate) return
    const timeout = window.setTimeout(() => {
      void performSave()
    }, 1200)
    return () => window.clearTimeout(timeout)
  }, [
    canEditCurrentDocument,
    disablePersistence,
    isCanvasOpen,
    isDirty,
    pendingCount,
    pendingRemoteUpdate,
    performSave,
  ])

  const handleClose = useCallback(() => {
    closeSidebar()
  }, [closeSidebar])

  const handleDeleteDocument = useCallback(async () => {
    if (!documentId || deleteDocumentMutation.isPending) {
      return
    }

    try {
      await deleteDocumentMutation.mutateAsync(documentId)
      setShowDeleteDocumentDialog(false)
      closeSidebar()
      if (isDocumentMode && onBackToDocuments) {
        onBackToDocuments()
        return
      }
      void navigate("/documents")
    } catch {
      // Error toast handled by useDeleteDocument
    }
  }, [
    closeSidebar,
    deleteDocumentMutation,
    documentId,
    isDocumentMode,
    navigate,
    onBackToDocuments,
  ])

  useEffect(() => {
    if (blocker.state !== "blocked") {
      return
    }

    let cancelled = false
    void requestSidebarClose().then((shouldProceed) => {
      if (cancelled) {
        return
      }
      if (shouldProceed) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    })

    return () => {
      cancelled = true
    }
  }, [blocker, requestSidebarClose])

  const handleRawMarkdownChange = useCallback(
    (newMarkdown: string) => {
      setContent(newMarkdown)
      setIsDirty(true)
      if (editor) {
        const html = markdownToTiptapHtml(newMarkdown)
        editor.commands.setContent(html, { emitUpdate: false })
      }
    },
    [editor, setContent]
  )

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content)
      toast.success(t("canvas.copied", { defaultValue: "Copied" }), {
        description: t("canvas.copied_description", {
          defaultValue: "Content copied to clipboard",
        }),
      })
    } catch {
      toast.error(t("canvas.copy_failed", { defaultValue: "Copy failed" }), {
        description: t("canvas.copy_failed_description", {
          defaultValue: "Could not copy to clipboard",
        }),
      })
    }
  }, [content, t])

  const handleDownloadWord = useCallback(
    async (useTemplate: boolean = false) => {
      try {
        const settingsService = SettingsService.getInstance()
        const processedContent = replaceCitationsWithLinks(content, "md")
        const filename = title.trim().replace(/[<>:"/\\|?*]/g, "_") || `canvas-${Date.now()}`
        const response = await downloadResponseApi(
          settingsService.token,
          processedContent,
          "md",
          "docx",
          filename,
          useTemplate,
          sessionId ?? undefined
        )

        const blob = await response.blob()
        const { saveAs } = await import("file-saver")
        saveAs(blob, `${filename}.docx`)

        toast.success(t("canvas.download_success", { defaultValue: "Downloaded" }), {
          description: t("canvas.download_success_description", {
            defaultValue: "Document downloaded successfully",
          }),
        })
      } catch (error) {
        toast.error(t("canvas.download_failed", { defaultValue: "Download failed" }), {
          description:
            error instanceof Error
              ? error.message
              : t("canvas.download_failed_description", {
                  defaultValue: "Failed to download document",
                }),
        })
      }
    },
    [content, sessionId, t]
  )

  const generateSuggestionId = useCallback(() => {
    return `suggestion-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  }, [])

  const applyFullDocAsSuggestion = useCallback(
    (modifiedMarkdown: string, previewText: string) => {
      if (!editor) return

      if (pendingSuggestions.size > 0) {
        toast.error(t("canvas.ai_blocked", { defaultValue: "Cannot apply edit" }), {
          description: t("canvas.ai_pending_suggestions", {
            defaultValue: "Accept or discard existing suggestions first",
          }),
        })
        return
      }

      const originalMarkdown = content
      const originalHtml = editor.getHTML()

      const newHtml = markdownToTiptapHtml(modifiedMarkdown)
      editor.commands.setContent(newHtml, { emitUpdate: false })

      const docSize = editor.state.doc.content.size
      const highlightFrom = 0
      const highlightTo = docSize

      if (highlightFrom >= highlightTo) {
        setContent(modifiedMarkdown)
        setIsDirty(true)
        return
      }

      const suggestionId = generateSuggestionId()
      const highlightRange = { from: highlightFrom, to: highlightTo }

      const meta: AiEditHighlightMeta = {
        action: "add",
        id: suggestionId,
        ranges: [highlightRange],
      }
      editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))

      handleSuggestionCreated({
        id: suggestionId,
        originalHtml,
        originalText: originalMarkdown,
        modifiedText: modifiedMarkdown,
        from: highlightFrom,
        to: highlightTo,
      })

      toast.success(t("canvas.ai_suggestion_created", { defaultValue: "AI Suggestion Created" }), {
        description: previewText,
      })
    },
    [editor, content, pendingSuggestions, generateSuggestionId, handleSuggestionCreated, t]
  )

  const captureScopedAiEdit = useCallback(
    (target: SelectionEditTarget): ScopedAiEditSource | null => {
      if (!editor) return null
      const { state } = editor
      const refuse = (description: string) => {
        toast.error(t("canvas.ai_blocked", { defaultValue: "Cannot apply edit" }), { description })
        return null
      }

      if (!target.cellRanges && selectionCrossesTableCells(state.doc, target.from, target.to)) {
        return refuse(
          t("canvas.ai_selection_crosses_cells", {
            defaultValue: "Select text inside one table cell, or select whole cells.",
          })
        )
      }
      const highlightState = aiEditHighlightKey.getState(state) as AiEditHighlightState | null
      if (
        highlightState &&
        isSelectionOverlappingAnySuggestion(highlightState, target.from, target.to)
      ) {
        return refuse(
          t("canvas.ai_edit_overlap_warning", {
            defaultValue: "Selection overlaps pending suggestion",
          })
        )
      }

      if (isMultiCellTarget(target)) {
        const originalCellTexts = (target.cellRanges ?? []).map((range) =>
          state.doc.textBetween(range.from, range.to, "")
        )
        return {
          target,
          doc: state.doc,
          originalHtml: "",
          originalMarkdown: originalCellTexts.join("\n"),
          originalCellTexts,
        }
      }

      const originalHtml = serializeRangeToHtml(state.doc, target)
      return {
        target,
        doc: state.doc,
        originalHtml,
        originalMarkdown: turndownService.turndown(originalHtml),
        originalCellTexts: [],
      }
    },
    [editor, t, turndownService]
  )

  // With a selection an AI action works on that range only and its result is
  // reviewed as a diff before it lands; without one it rewrites the whole document.
  const runAiTextAction = useCallback(
    async (
      request: (text: string) => Promise<TextEnhancementResponse>,
      labels: AiTextActionLabels
    ) => {
      if (!editor || isAiProcessing) return

      const target = getSelectionEditTarget(editor.state.selection)
      const scoped = target ? captureScopedAiEdit(target) : null
      if (target && !scoped) return

      setIsAiProcessing(true)
      try {
        const result = await request(scoped ? scoped.originalMarkdown : content)
        if (!result.success || !result.modified) {
          throw new Error(result.error || labels.failureMessage)
        }
        if (!scoped) {
          applyFullDocAsSuggestion(result.modified, labels.suggestionMessage)
          return
        }
        if (!result.modified.trim()) {
          throw new Error(
            t("canvas.ai_empty_result", {
              defaultValue: "The assistant returned an empty response.",
            })
          )
        }
        setScopedAiEdit({ ...scoped, ...labels, modifiedText: result.modified })
      } catch (error) {
        toast.error(t("canvas.ai_failed", { defaultValue: "AI Edit Failed" }), {
          description: error instanceof Error ? error.message : "Unknown error",
        })
      } finally {
        setIsAiProcessing(false)
      }
    },
    [editor, isAiProcessing, content, captureScopedAiEdit, applyFullDocAsSuggestion, t]
  )

  const applyScopedAiEdit = useCallback(() => {
    const edit = scopedAiEdit
    setScopedAiEdit(null)
    if (!editor || !edit) return

    const refuse = (description: string) => {
      toast.error(t("canvas.ai_blocked", { defaultValue: "Cannot apply edit" }), { description })
    }
    // Positions were taken from the document the request was made against.
    if (!editor.state.doc.eq(edit.doc)) {
      refuse(
        t("canvas.ai_document_changed", {
          defaultValue: "The document changed while the AI was working. Run the action again.",
        })
      )
      return
    }

    const { target } = edit
    let ranges: Array<{ from: number; to: number }>
    if (isMultiCellTarget(target) && target.cellRanges) {
      const cellValues = parseResponseIntoCellValues(
        normalizeInsertedMarkdown(edit.modifiedText),
        target.cellRanges.length,
        edit.originalCellTexts
      )
      ranges = applyCellReplacements(editor, target.cellRanges, cellValues)
    } else {
      const replacement = buildRangeReplacement(editor.state, target, {
        originalHtml: edit.originalHtml,
        insertedHtml: markdownToInsertableHtml(edit.modifiedText),
      })
      if (!replacement) {
        refuse(
          t("canvas.ai_selection_unsafe", {
            defaultValue:
              "This change can't be applied to the selection safely. Select text within one section, or clear the selection to edit the whole document.",
          })
        )
        return
      }
      editor.view.dispatch(replacement.tr)
      ranges = [replacement.range]
    }

    const suggestionId = generateSuggestionId()
    const meta: AiEditHighlightMeta = { action: "add", id: suggestionId, ranges }
    editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))

    handleSuggestionCreated({
      id: suggestionId,
      originalHtml: edit.originalHtml,
      originalText: edit.originalMarkdown,
      modifiedText: edit.modifiedText,
      from: Math.min(...ranges.map((range) => range.from)),
      to: Math.max(...ranges.map((range) => range.to)),
      ...(isMultiCellTarget(target)
        ? { cellRanges: ranges, originalCellTexts: edit.originalCellTexts }
        : {}),
    })

    toast.success(t("canvas.ai_suggestion_created", { defaultValue: "AI Suggestion Created" }), {
      description: edit.suggestionMessage,
    })
  }, [editor, scopedAiEdit, generateSuggestionId, handleSuggestionCreated, t])

  const handlePolish = useCallback(async () => {
    const modelId = selectedModelId ?? undefined
    await runAiTextAction(
      (text) =>
        polishTextApi(
          text,
          undefined,
          undefined,
          undefined,
          SettingsService.getInstance().token,
          modelId
        ),
      {
        operationName: t("canvas.ai_polish", { defaultValue: "Polish text" }),
        suggestionMessage: t("canvas.ai_polish_suggestion", {
          defaultValue: "Polish text suggestion ready",
        }),
        failureMessage: "Failed to polish text",
      }
    )
  }, [runAiTextAction, selectedModelId, t])

  const handleAddEmojis = useCallback(
    async (target: "words" | "sections" | "lists" | "remove") => {
      if (!editor || isAiProcessing) return
      setIsAiProcessing(true)
      try {
        const settingsService = SettingsService.getInstance()
        const result = await addEmojisApi(
          content,
          target,
          undefined,
          undefined,
          undefined,
          settingsService.token,
          selectedModelId ?? undefined
        )
        if (result.success && result.modified) {
          const previewText =
            target === "remove"
              ? t("canvas.ai_emojis_removed_suggestion", {
                  defaultValue: "Remove emojis suggestion ready",
                })
              : t("canvas.ai_emojis_added_suggestion", {
                  defaultValue: "Add emojis suggestion ready",
                })
          applyFullDocAsSuggestion(result.modified, previewText)
        } else {
          throw new Error(result.error || "Failed to add emojis")
        }
      } catch (error) {
        toast.error(t("canvas.ai_failed", { defaultValue: "AI Edit Failed" }), {
          description: error instanceof Error ? error.message : "Unknown error",
        })
      } finally {
        setIsAiProcessing(false)
      }
    },
    [editor, isAiProcessing, content, selectedModelId, applyFullDocAsSuggestion, t]
  )

  const handleReadingLevelChange = useCallback(
    async (
      level:
        | "kindergarten"
        | "elementary"
        | "middle_school"
        | "high_school"
        | "college"
        | "graduate"
    ) => {
      const modelId = selectedModelId ?? undefined
      await runAiTextAction(
        (text) =>
          adjustReadingLevelApi(
            text,
            level,
            undefined,
            undefined,
            undefined,
            SettingsService.getInstance().token,
            modelId
          ),
        {
          operationName: t("canvas.ai_reading_level", { defaultValue: "Adjust reading level" }),
          suggestionMessage: t("canvas.ai_reading_level_suggestion", {
            defaultValue: "Reading level suggestion ready",
          }),
          failureMessage: "Failed to adjust reading level",
        }
      )
    },
    [runAiTextAction, selectedModelId, t]
  )

  const handleLengthChange = useCallback(
    async (length: "much_shorter" | "shorter" | "keep" | "longer" | "much_longer") => {
      if (length === "keep") return
      const modelId = selectedModelId ?? undefined
      await runAiTextAction(
        (text) =>
          adjustLengthApi(
            text,
            length,
            undefined,
            undefined,
            undefined,
            SettingsService.getInstance().token,
            modelId
          ),
        {
          operationName: t("canvas.ai_adjust_length", { defaultValue: "Adjust length" }),
          suggestionMessage: t("canvas.ai_length_suggestion", {
            defaultValue: "Length adjustment suggestion ready",
          }),
          failureMessage: "Failed to adjust length",
        }
      )
    },
    [runAiTextAction, selectedModelId, t]
  )

  const handleCustomAiEdit = useCallback(
    async (instruction: string) => {
      const trimmedInstruction = instruction.trim()
      if (!trimmedInstruction) return
      const modelId = selectedModelId ?? undefined
      await runAiTextAction(
        (text) =>
          suggestEditApi(
            text,
            trimmedInstruction,
            undefined,
            undefined,
            undefined,
            SettingsService.getInstance().token,
            modelId
          ),
        {
          operationName: t("canvas.ai_edit_operation", { defaultValue: "AI edit" }),
          suggestionMessage: t("canvas.ai_custom_suggestion", {
            defaultValue: "Custom edit suggestion ready",
          }),
          failureMessage: "Failed to apply custom edit",
        }
      )
    },
    [runAiTextAction, selectedModelId, t]
  )

  const fileToDataUrl = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(new Error("Failed to read file"))
      reader.readAsDataURL(file)
    })
  }, [])

  const handleLocalImageSelected = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || !editor) return

      const file = files[0]
      const maxSize = 10 * 1024 * 1024 // 10MB

      if (file.size > maxSize) {
        toast.error(t("canvas.image_too_large", { defaultValue: "File too large" }), {
          description: t("canvas.image_size_limit", {
            defaultValue: "Image must be less than 10MB",
          }),
        })
        return
      }

      setIsUploadingImage(true)
      try {
        const dataUrl = await fileToDataUrl(file)
        editor.chain().focus().setImage({ src: dataUrl, alt: file.name }).run()
        setIsDirty(true)

        toast.success(t("canvas.image", { defaultValue: "Image" }), {
          description: t("canvas.image_added", { defaultValue: "Image added successfully" }),
        })
        setShowFilePicker(false)
      } catch (error) {
        toast.error(t("canvas.upload_failed", { defaultValue: "Upload failed" }), {
          description:
            error instanceof Error
              ? error.message
              : t("canvas.failed_add_image", { defaultValue: "Failed to add image" }),
        })
      } finally {
        setIsUploadingImage(false)
      }
    },
    [editor, fileToDataUrl, t]
  )

  const handleGraphImageSelected = useCallback(
    async (item: GraphFileItem) => {
      if (!editor) return

      setIsUploadingImage(true)
      try {
        const settingsService = SettingsService.getInstance()
        const blob = await downloadGraphFileApi(item, settingsService.token)
        const dataUrl = await fileToDataUrl(new File([blob], item.name, { type: item.mimeType }))

        editor.chain().focus().setImage({ src: dataUrl, alt: item.name }).run()
        setIsDirty(true)

        toast.success(t("canvas.image", { defaultValue: "Image" }), {
          description: t("canvas.image_added", { defaultValue: "Image added successfully" }),
        })
        setShowFilePicker(false)
      } catch (error) {
        toast.error(t("canvas.upload_failed", { defaultValue: "Upload failed" }), {
          description:
            error instanceof Error
              ? error.message
              : t("canvas.failed_add_image", { defaultValue: "Failed to add image" }),
        })
      } finally {
        setIsUploadingImage(false)
      }
    },
    [editor, fileToDataUrl, t]
  )

  useEffect(() => {
    if (!editor || !pendingRemoteUpdate || hasAppliedRemotePreviewRef.current) return
    if (pendingSuggestions.size > 0) return

    remotePreviewSnapshotRef.current = {
      html: editor.getHTML(),
      markdown: content,
      isDirty,
    }

    const previewHtml = markdownToTiptapHtml(
      pendingRemoteUpdate.nextContent,
      sessionId || imageContentBaseUrl
        ? {
            sessionId: sessionId ?? "",
            filenames: sessionFilenames,
            contentBaseUrl: imageContentBaseUrl ?? undefined,
          }
        : undefined
    )
    editor.commands.setContent(previewHtml, { emitUpdate: false })

    const docSize = editor.state.doc.content.size
    if (docSize > 0) {
      const meta: AiEditHighlightMeta = {
        action: "add",
        id: REMOTE_UPDATE_HIGHLIGHT_ID,
        ranges: [{ from: 0, to: docSize }],
      }
      editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))
    }

    setContent(pendingRemoteUpdate.nextContent)
    setIsDirty(true)
    hasAppliedRemotePreviewRef.current = true
  }, [
    content,
    editor,
    isDirty,
    pendingRemoteUpdate,
    pendingSuggestions.size,
    sessionFilenames,
    sessionId,
    setContent,
  ])

  useEffect(() => {
    if (!editor || pendingRemoteUpdate || !hasAppliedRemotePreviewRef.current) return
    const meta: AiEditHighlightMeta = { action: "remove", id: REMOTE_UPDATE_HIGHLIGHT_ID }
    editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))
    hasAppliedRemotePreviewRef.current = false
    remotePreviewSnapshotRef.current = null
  }, [editor, pendingRemoteUpdate])

  const handleKeepRemoteChanges = useCallback(() => {
    applyRemoteUpdate()
    setShowRemoteDiffDialog(false)
  }, [applyRemoteUpdate])

  const handleRejectRemoteChanges = useCallback(() => {
    if (editor) {
      const snapshot = remotePreviewSnapshotRef.current
      const restoredMarkdown = snapshot?.markdown ?? pendingRemoteUpdate?.previousContent
      if (typeof restoredMarkdown === "string") {
        const restoredHtml =
          snapshot?.html ??
          markdownToTiptapHtml(
            restoredMarkdown,
            sessionId || imageContentBaseUrl
              ? {
                  sessionId: sessionId ?? "",
                  filenames: sessionFilenames,
                  contentBaseUrl: imageContentBaseUrl ?? undefined,
                }
              : undefined
          )
        editor.commands.setContent(restoredHtml, { emitUpdate: false })
        setContent(restoredMarkdown)
      }
      if (snapshot) {
        setIsDirty(snapshot.isDirty)
      }
    }

    dismissRemoteUpdate()
    setShowRemoteDiffDialog(false)
  }, [dismissRemoteUpdate, editor, pendingRemoteUpdate, sessionFilenames, sessionId, setContent])

  const handleBackAction = useCallback(() => {
    if (isDocumentMode && onBackToDocuments) {
      onBackToDocuments()
      return
    }

    if (isPhone && isFocused && onReturnToChat) {
      onReturnToChat()
      return
    }

    handleClose()
  }, [handleClose, isDocumentMode, isFocused, isPhone, onBackToDocuments, onReturnToChat])
  const canShowShareDocumentAction = Boolean(documentId && onShareDocument && canShareDocument)
  const canShowDeleteDocumentAction = Boolean(isDocumentMode && documentId && canDeleteDocument)
  const resolvedBackButtonLabel = backButtonLabel ?? t("canvas.back", { defaultValue: "Back" })
  const resolvedStartChatButtonLabel =
    startChatButtonLabel ??
    t("documents.chat_with_document", {
      defaultValue: "Chat with document",
    })
  const chatWithDocumentButtonClassName = cn(
    "group/document-chat text-foreground h-9 rounded-lg px-2 text-sm transition-[color,background-color,scale] duration-150",
    "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
    "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1",
    "active:scale-[0.98]",
    isPhone ? "w-8 px-0" : "justify-start"
  )

  const previewParsed = useMemo(() => {
    if (viewMode !== "preview") return { answerHtml: "", citations: [] as string[] }
    const libraryIdFallback = content.match(/\[\[([^\]]+)\]\]/)?.[1]?.split(":")[0] || "library"
    const citationSection = sessionId ?? libraryIdFallback
    return parseAnswerToHtml({
      answer: content,
      isStreaming: false,
      noCitation: false,
      citationSection,
    })
  }, [content, sessionId, viewMode])

  const previewHtml = previewParsed.answerHtml

  const previewCitationComponents = useMemo(() => {
    if (previewParsed.citations.length === 0) return undefined
    const libraryIdFallback = content.match(/\[\[([^\]]+)\]\]/)?.[1]?.split(":")[0] || "library"
    const citationSection = sessionId ?? libraryIdFallback
    return {
      a: createCitationLinkComponent(
        () => previewParsed.citations,
        () => citationSection
      ),
    }
  }, [previewParsed.citations, content, sessionId])

  const handlePreviewCitationClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>(".supContainer")
      if (!button) return
      const rawCitation = button.dataset.citationRaw
      if (!rawCitation) return

      const citationType = getCitationType(rawCitation)
      if (citationType !== "library") return

      const parsed = parseCitationParts(rawCitation)
      const libraryIdFallback = parsed.source.split(":")[0] || "library"
      const citationSection = sessionId ?? libraryIdFallback
      const documentUrl =
        getCitationFilePath(parsed.source, citationSection) +
        (parsed.page != null ? `#page=${parsed.page}` : "")
      const citationTitle = resolveCitationDisplayTitle(rawCitation)
      const quoteSnippet = parsed.quote?.trim() ? normalizeForMatching(parsed.quote) : ""

      useContentSidebarStore.getState().pushPanel({
        id: "citation-preview",
        title:
          citationTitle || i18n.t("analysispanel.citation", { defaultValue: "Cited document" }),
        headerMode: "custom",
        renderHeader: (context) => (
          <CitationSidebarHeader
            closeable={context.closeable}
            downloadUrl={documentUrl}
            isFocusable={context.isFocusable}
            isFocused={context.isFocused}
            title={citationTitle}
            onBack={() => useContentSidebarStore.getState().popPanel()}
            onClose={context.onClose}
            onToggleFocus={context.onToggleFocus}
          />
        ),
        content: (
          <CitationViewerContent
            chunkSnippet=""
            documentUrl={documentUrl}
            quoteSnippet={quoteSnippet}
          />
        ),
        focusable: true,
        closeable: true,
        resizable: true,
        isDirty: () => false,
      })
    },
    [sessionId]
  )

  return (
    <section className="bg-muted flex h-full flex-col">
      {/* Canvas Header - Minimal Design */}
      <header className="border-border/40 bg-background flex h-16 shrink-0 items-center justify-between gap-2 border-b px-3 sm:gap-4 sm:px-5">
        {/* Mobile keeps text back button; document route uses icon-only + tooltip */}
        {isDocumentMode ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={resolvedBackButtonLabel}
                className="h-8 w-8"
                size="icon"
                variant="ghost"
                onClick={handleBackAction}
              >
                <ArrowLeft className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{resolvedBackButtonLabel}</TooltipContent>
          </Tooltip>
        ) : (
          isPhone && (
            <Button
              className="h-8 gap-1.5 px-2"
              size="sm"
              variant="ghost"
              onClick={handleBackAction}
            >
              <ArrowLeft className="size-4" />
              <span className="text-sm">{resolvedBackButtonLabel}</span>
            </Button>
          )
        )}

        {lockedByOther && (
          <Badge
            className="border-warning-ink/40 bg-warning/30 text-warning-ink h-7 shrink-0 px-3"
            variant="outline"
          >
            <Eye className="mr-1.5 size-3" />
            {t("artifacts.locked_by", {
              name: artifactLock.lockedBy?.displayName ?? "Someone",
              defaultValue: "{{name}} is editing…",
            })}
          </Badge>
        )}

        {!canEditCurrentDocument && !lockedByOther && (
          <Badge className={cn("h-7 shrink-0 px-3", STATUS_TONES.info.solid)} variant="outline">
            <Eye className="mr-1.5 size-3" />
            {t("chatsession.reader_badge", { defaultValue: "READER" })}
          </Badge>
        )}

        <div className={cn("min-w-0 flex-1", isPhone && "text-center")}>
          {isRenamingTitle ? (
            <form
              className={cn("flex max-w-full items-center gap-1", isPhone && "justify-center")}
              onSubmit={handleTitleRenameFormSubmit}
            >
              <Input
                ref={titleInputRef}
                aria-label={t("canvas.rename_document", { defaultValue: "Rename document" })}
                className="h-8 max-w-[520px] text-sm font-semibold sm:text-base"
                disabled={isSavingTitle}
                maxLength={120}
                value={titleDraft}
                onChange={(event) => setTitleDraft(event.target.value)}
                onKeyDown={handleTitleRenameKeyDown}
              />
              <Button
                aria-label={t("canvas.rename_confirm", { defaultValue: "Confirm name" })}
                className="h-8 w-8 shrink-0"
                disabled={isSavingTitle || titleDraft.trim().length === 0}
                size="icon"
                type="submit"
                variant="ghost"
              >
                {isSavingTitle ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Check className="size-4" />
                )}
              </Button>
              <Button
                aria-label={t("canvas.rename_cancel", { defaultValue: "Cancel renaming" })}
                className="h-8 w-8 shrink-0"
                disabled={isSavingTitle}
                size="icon"
                type="button"
                variant="ghost"
                onClick={handleCancelTitleRename}
              >
                <X className="size-4" />
              </Button>
            </form>
          ) : (
            <h1 className="text-foreground text-base font-semibold tracking-tight">
              {canRenameTitle ? (
                <button
                  className={renameTitleButtonClassName}
                  title={t("canvas.rename_hint", { defaultValue: "Click to rename document" })}
                  type="button"
                  onClick={handleStartTitleRename}
                >
                  <span className="truncate">{title}</span>
                  <Pencil className="text-muted-foreground size-3.5 shrink-0 opacity-0 transition-opacity group-focus-within/title:opacity-100 group-hover/title:opacity-100" />
                </button>
              ) : (
                <span className="truncate">{title}</span>
              )}
            </h1>
          )}
          {!isPhone && (
            <p className={cn("text-muted-foreground truncate text-xs", canRenameTitle && "px-2")}>
              {lastUpdatedLabel}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-1.5">
          {showStartNewChatWithDocument && (
            <Button
              className={chatWithDocumentButtonClassName}
              size="sm"
              title={resolvedStartChatButtonLabel}
              variant="ghost"
              onClick={onStartNewChatWithDocument}
            >
              <div className={cn("flex items-center gap-2", isPhone && "justify-center")}>
                <MessageCirclePlus className="size-4 shrink-0" />
                {!isPhone && (
                  <span className="translate-y-px truncate font-semibold">
                    {resolvedStartChatButtonLabel}
                  </span>
                )}
              </div>
            </Button>
          )}

          {/* More Actions Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-8 w-8" size="icon" variant="ghost">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {/* View Mode Section */}
              <DropdownMenuItem className="gap-2" onClick={() => setViewMode("preview")}>
                <Eye className="size-4" />
                {t("canvas.preview_mode", { defaultValue: "Preview" })}
                {viewMode === "preview" && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
              {canEditCurrentDocument && (
                <>
                  <DropdownMenuItem className="gap-2" onClick={() => setViewMode("edit")}>
                    <Pencil className="size-4" />
                    {t("canvas.edit_mode", { defaultValue: "Edit" })}
                    {viewMode === "edit" && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2" onClick={() => setViewMode("raw")}>
                    <Code className="size-4" />
                    {t("canvas.raw_markdown", { defaultValue: "Raw Markdown" })}
                    {viewMode === "raw" && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuSeparator />

              {/* Edit Mode Options - hide formatting toggle on mobile since we use bottom bar */}
              {canEditCurrentDocument && viewMode === "edit" && !isPhone && (
                <>
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={() => setShowFormatToolbar(!showFormatToolbar)}
                  >
                    <Type className="size-4" />
                    {t("canvas.toggle_formatting", { defaultValue: "Formatting toolbar" })}
                    {showFormatToolbar && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              {/* Common Actions */}
              <DropdownMenuItem className="gap-2" onClick={handleCopy}>
                <Copy className="size-4" />
                {t("canvas.copy", { defaultValue: "Copy to clipboard" })}
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onClick={() => setIsHistoryOpen(true)}>
                <History className="size-4" />
                {t("canvas.history", { defaultValue: "Version history" })}
              </DropdownMenuItem>
              {canShowShareDocumentAction && (
                <DropdownMenuItem className="gap-2" onClick={() => onShareDocument?.()}>
                  <Share2 className="size-4" />
                  {t("sharing.share", { defaultValue: "Share" })}
                </DropdownMenuItem>
              )}
              {canShowDeleteDocumentAction && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive gap-2"
                    disabled={deleteDocumentMutation.isPending}
                    onClick={() => setShowDeleteDocumentDialog(true)}
                  >
                    <Trash2 className="size-4" />
                    {t("canvas.delete_document", { defaultValue: "Delete document" })}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Download Menu */}
          <DownloadMenu
            disabled={isSaving}
            hasTemplate={Boolean(config?.docxTemplateEnabled)}
            onDownloadWord={handleDownloadWord}
          />

          {/* Maximize/Minimize toggle - desktop split view only */}
          {!isPhone && !isDocumentMode && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="h-8 w-8"
                  size="icon"
                  variant="ghost"
                  onClick={toggleSidebarFocus}
                >
                  {isFocused ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isFocused
                  ? t("canvas.minimize", { defaultValue: "Minimize" })
                  : t("canvas.maximize", { defaultValue: "Maximize" })}
              </TooltipContent>
            </Tooltip>
          )}

          <div
            className={cn(
              "w-[9rem] rounded-md px-2 py-1 text-center text-xs font-medium",
              autosaveStatus === "error" ? "text-destructive" : "text-muted-foreground",
              isPhone && "hidden"
            )}
          >
            <span
              className={cn(
                "inline-flex w-full items-center justify-center whitespace-nowrap",
                isAutosaveSaving &&
                  "animate-shimmer from-muted-foreground via-foreground to-muted-foreground bg-linear-to-r bg-size-[200%_100%] bg-clip-text text-transparent"
              )}
            >
              {autosaveLabel}
            </span>
          </div>

          {/* Close button - desktop only (mobile uses Back button on left) */}
          {!isPhone && !isDocumentMode && (
            <Button className="h-8 w-8" size="icon" variant="ghost" onClick={handleClose}>
              <X className="size-4" />
            </Button>
          )}
        </div>
      </header>

      {/* Pending suggestions banner */}
      {pendingCount > 0 && (
        <div className={cn("sticky top-0 z-10 pt-2", isPhone ? "px-3" : "px-6")}>
          <div className="mx-auto max-w-[900px]">
            <div className="bg-warning/15 border-warning text-foreground flex items-center justify-between gap-2 rounded-xl border px-3 py-2 shadow-[0_2px_8px_rgba(0,0,0,0.06)] backdrop-blur sm:gap-3 sm:px-4 sm:py-2.5">
              <div className="text-warning flex items-center gap-2 text-xs font-medium sm:text-sm">
                <Sparkles className="size-3 sm:size-4" />
                <span>
                  {pendingCount === 1
                    ? t("canvas.suggestions_pending_one", {
                        defaultValue: "1 AI suggestion pending",
                      })
                    : t("canvas.suggestions_pending_many", {
                        defaultValue: "{{count}} AI suggestions pending",
                        count: pendingCount,
                      })}
                </span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  className="h-6 text-xs sm:h-7"
                  size="sm"
                  variant="ghost"
                  onClick={acceptAllSuggestions}
                >
                  {t("canvas.accept_all", { defaultValue: "Accept all" })}
                </Button>
                <Button
                  className="h-6 text-xs sm:h-7"
                  size="sm"
                  variant="ghost"
                  onClick={discardAllSuggestions}
                >
                  {t("canvas.discard_all", { defaultValue: "Discard all" })}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Editor Content */}
      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-contain pt-4 sm:pt-6",
          isPhone ? "px-2 pb-24" : "px-6 pb-12 sm:px-8"
        )}
      >
        <div className="relative mx-auto w-full max-w-[900px]">
          {viewMode === "edit" && showFormatToolbar && !isPhone && (
            <>
              <div className="pointer-events-none sticky top-0 z-20 h-0 min-w-0">
                <div className="pointer-events-auto min-w-0">
                  <FormatToolbar
                    disabled={isSaving || isUploadingImage}
                    editor={editor}
                    isAiProcessing={isAiProcessing}
                    onAddEmojis={handleAddEmojis}
                    onCustomAiEdit={handleCustomAiEdit}
                    onImageUpload={() => setShowFilePicker(true)}
                    onLengthChange={handleLengthChange}
                    onPolish={handlePolish}
                    onReadingLevelChange={handleReadingLevelChange}
                  />
                </div>
              </div>

              {/* Spacer so the toolbar starts above the doc, but scrolls away so it can overlay later */}
              <div className="h-14" />
            </>
          )}

          <div
            className={cn(
              "bg-card text-card-foreground w-full overflow-hidden transition-colors duration-200",
              isPhone
                ? "rounded-xl shadow-sm"
                : "rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
            )}
          >
            {pendingRemoteUpdate && (
              <div className="border-primary bg-primary/15 text-foreground border-b px-4 py-3 sm:px-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground text-sm font-semibold">
                      {t("canvas.remote_update_available", {
                        defaultValue: "AI updated this document in the background",
                      })}
                    </p>
                    <p className="text-foreground truncate text-xs">
                      {pendingRemoteUpdate.changeSummary ||
                        t("canvas.ai_changes_summary_fallback", {
                          defaultValue: "Review and choose how to handle the AI changes.",
                        })}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowRemoteDiffDialog(true)}
                    >
                      {t("canvas.ai_view_diff", { defaultValue: "View diff" })}
                    </Button>
                    <Button size="sm" onClick={handleKeepRemoteChanges}>
                      {t("canvas.keep_changes", { defaultValue: "Keep changes" })}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleRejectRemoteChanges}>
                      {t("canvas.reject_changes", { defaultValue: "Reject changes" })}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {canEditCurrentDocument && viewMode === "edit" && (
              <div
                ref={editorContainerRef}
                className={cn("relative", isPhone ? "px-4 py-5" : "px-8 py-10 sm:px-12 sm:py-12")}
              >
                <EditorContent
                  editor={editor}
                  className={cn(
                    answerStyles.answerText,
                    "prose dark:prose-invert focus:outline-none",
                    "[&_.ProseMirror]:min-h-[60vh] [&_.ProseMirror]:outline-none"
                  )}
                />
                <AiEditBubble
                  documentContent={content}
                  documentId={documentId}
                  editor={editor}
                  modelId={selectedModelId}
                  sessionId={sessionId}
                  onSuggestionCreated={handleSuggestionCreated}
                />
                <InlineSuggestionActions
                  containerRef={editorContainerRef}
                  editor={editor}
                  pendingSuggestions={pendingSuggestions}
                  onAccept={acceptSuggestion}
                  onDiscard={discardSuggestion}
                />
              </div>
            )}

            {viewMode === "preview" && (
              <div className={cn(isPhone ? "px-4 py-5" : "px-8 py-10 sm:px-12 sm:py-12")}>
                <div
                  className={cn(answerStyles.answerText, "prose dark:prose-invert min-h-[60vh]")}
                  role="presentation"
                  onClick={handlePreviewCitationClick}
                >
                  <MarkdownRenderer
                    components={previewCitationComponents}
                    content={previewHtml}
                    sessionImageContext={
                      sessionId || imageContentBaseUrl
                        ? {
                            sessionId: sessionId ?? "",
                            filenames: sessionFilenames,
                            contentBaseUrl: imageContentBaseUrl ?? undefined,
                          }
                        : undefined
                    }
                  />
                </div>
              </div>
            )}

            {canEditCurrentDocument && viewMode === "raw" && (
              <div className={cn(isPhone ? "px-4 py-5" : "px-8 py-10 sm:px-12 sm:py-12")}>
                <Textarea
                  className="min-h-[60vh] w-full resize-none border-none bg-transparent font-mono text-sm focus-visible:ring-0"
                  disabled={isSaving}
                  value={content}
                  placeholder={t("canvas.raw_markdown_placeholder", {
                    defaultValue: "Edit raw markdown...",
                  })}
                  onChange={(event) => handleRawMarkdownChange(event.target.value)}
                />
              </div>
            )}

            {editor && viewMode !== "raw" && <CanvasReferencesPanel editor={editor} />}
          </div>
        </div>
      </div>

      {pendingRemoteUpdate && (
        <DiffPreviewDialog
          modified={pendingRemoteUpdate.nextContent}
          open={showRemoteDiffDialog}
          original={pendingRemoteUpdate.previousContent}
          operationName={t("canvas.ai_changes_diff_operation", {
            defaultValue: "AI changes",
          })}
          onAccept={handleKeepRemoteChanges}
          onOpenChange={setShowRemoteDiffDialog}
          onReject={handleRejectRemoteChanges}
        />
      )}

      {scopedAiEdit && (
        <DiffPreviewDialog
          open
          modified={normalizeInsertedMarkdown(scopedAiEdit.modifiedText)}
          operationName={scopedAiEdit.operationName}
          original={scopedAiEdit.originalMarkdown}
          onAccept={applyScopedAiEdit}
          onReject={() => setScopedAiEdit(null)}
          onOpenChange={(open) => {
            if (!open) setScopedAiEdit(null)
          }}
        />
      )}

      <CanvasHistoryPanel
        canRestore={canEditCurrentDocument}
        currentContent={content}
        isOpen={isHistoryOpen}
        isPhone={isPhone}
        versions={versions}
        onClose={() => setIsHistoryOpen(false)}
        onRestoreVersion={handleRestoreVersion}
      />

      {/* Mobile Format Bar - only in edit mode on phones */}
      {canEditCurrentDocument && viewMode === "edit" && isPhone && isActiveView && (
        <MobileFormatBar
          disabled={isSaving || isUploadingImage}
          editor={editor}
          isAiProcessing={isAiProcessing}
          onAddEmojis={handleAddEmojis}
          onCustomAiEdit={handleCustomAiEdit}
          onImageUpload={() => setShowFilePicker(true)}
          onLengthChange={handleLengthChange}
          onPolish={handlePolish}
          onReadingLevelChange={handleReadingLevelChange}
        />
      )}

      {/* File Picker Dialog for Image Upload */}
      <FilePickerDialog
        open={showFilePicker}
        source="oneDrive"
        accept={{
          "image/*": [".jpg", ".jpeg", ".png", ".gif", ".webp"],
        }}
        onLocalFilesSelected={handleLocalImageSelected}
        onOpenChange={setShowFilePicker}
        onSelect={handleGraphImageSelected}
      />

      <AlertDialog
        open={showDeleteDocumentDialog}
        onOpenChange={(open) => {
          if (deleteDocumentMutation.isPending) {
            return
          }
          setShowDeleteDocumentDialog(open)
        }}
      >
        <AlertDialogContent className="z-100" overlayClassName="z-[100]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("canvas.confirm_delete_document", { defaultValue: "Delete document?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("canvas.confirm_delete_document_description", {
                defaultValue:
                  'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
                name: title || t("documents.untitled", { defaultValue: "Untitled document" }),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDocumentMutation.isPending}>
              {t("canvas.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteDocumentMutation.isPending || !documentId}
              onClick={(event) => {
                event.preventDefault()
                void handleDeleteDocument()
              }}
            >
              {deleteDocumentMutation.isPending
                ? t("canvas.deleting_document", { defaultValue: "Deleting..." })
                : t("canvas.delete_document", { defaultValue: "Delete document" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
